
import { useState, useRef, useEffect } from 'react';
import { Package, Star, Video, ArrowRight, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type ServiceStepType = {
  title: string;
  description: string;
};

type ServiceType = {
  id: string;
  title: string;
  icon: React.ElementType;
  description: string;
  steps: ServiceStepType[];
  color: string;
  bgColor: string;
  videoUrl: string;
};

interface ServicesProps {
  disableAutoTrigger?: boolean;
}

const services: ServiceType[] = [
  {
    id: "barter",
    title: "Barter Collaborations",
    icon: Package,
    description: "Get authentic content in exchange for your products",
    color: "bg-woost-blue",
    bgColor: "bg-woost-soft-blue/20",
    videoUrl: "https://cdn.dribbble.com/users/1162077/screenshots/7201189/media/58582c1168d98d6c6f0752e6ff46495c.mp4",
    steps: [
      {
        title: "Share campaign brief",
        description: "Provide product details, campaign objectives, and creator requirements"
      },
      {
        title: "Select influencers",
        description: "Choose from our curated list of creators that match your brand"
      },
      {
        title: "Ship products",
        description: "Send your products directly to selected creators"
      },
      {
        title: "Influencers post",
        description: "Creators share authentic content featuring your product"
      },
      {
        title: "Review reports",
        description: "Analyze performance metrics and campaign results"
      }
    ]
  },
  {
    id: "reputation",
    title: "Marketplace Reputation",
    icon: Star,
    description: "Improve your Sales and Reputation on Top Marketplaces like Flipkart, Amazon, Blinkit with verified creators",
    color: "bg-woost-yellow",
    bgColor: "bg-woost-soft-yellow/20",
    videoUrl: "https://cdn.dribbble.com/userupload/4579426/file/original-ebd9907f89e178c0fad6e4bf5854f202.mp4",
    steps: [
      {
        title: "Select product(s) to boost",
        description: "Choose which listings need sales and reputation improvement"
      },
      {
        title: "Match with reviewers",
        description: "We pair your products with relevant verified creators"
      },
      {
        title: "Verified creators test & review",
        description: "Real users test your products and provide honest feedback"
      },
      {
        title: "Real-time feedback tracking",
        description: "Monitor improvement in sales and reputation as they come in"
      }
    ]
  },
  {
    id: "ugc",
    title: "UGC Content Creation",
    icon: Video,
    description: "Perfect for paid ads, reels, and brand website content needs",
    color: "bg-teal-500",
    bgColor: "bg-green-50",
    videoUrl: "https://cdn.dribbble.com/userupload/5187459/file/original-d9d946c39d6cc4ef6b42eb78eb13554d.mp4",
    steps: [
      {
        title: "Define content need",
        description: "Specify format, style, and messaging requirements"
      },
      {
        title: "Choose creators",
        description: "Select from our pool of UGC specialists based on your brief"
      },
      {
        title: "Approve script",
        description: "Review and finalize content direction before production"
      },
      {
        title: "Receive videos within days",
        description: "Get high-quality, ready-to-use content for your campaigns"
      }
    ]
  }
];

const ServicesSection = ({ disableAutoTrigger = false }: ServicesProps) => {
  const [activeTab, setActiveTab] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  
  const activeService = services.find(service => service.id === activeTab) || services[0];
  
  return (
    <section id="services" ref={sectionRef} className="py-20 relative overflow-hidden" style={{paddingTop: "50px"}}>
      <div className="container mx-auto px-4 md:px-6">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Services</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            End-to-end solutions for influencer marketing and content creation
          </p>
        </motion.div>
        
        <motion.div className="grid md:grid-cols-3 gap-6 mt-12">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              className={`bg-white rounded-xl p-6 shadow-md border border-gray-100 ${service.bgColor}`}
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0 },
              }}
              initial="hidden"
              animate={isVisible ? "visible" : "hidden"}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ 
                y: -5,
                boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
              }}
            >
              <div className={`p-3 rounded-lg w-12 h-12 flex items-center justify-center mb-4 ${service.color}`}>
                <service.icon className="text-white" size={24} />
              </div>
              <h3 className="text-xl font-semibold mb-2">{service.title}</h3>
              <p className="text-gray-600 mb-4">{service.description}</p>
              <Button
                variant="outline"
                onClick={() => setActiveTab(service.id)}
                className="w-full justify-center"
              >
                How It Works
              </Button>
            </motion.div>
          ))}
        </motion.div>
        
        {/* Service Modals */}
        {services.map((service) => (
          <Dialog 
            key={service.id}
            open={activeTab === service.id}
            onOpenChange={(open) => {
              if (!open) setActiveTab("");
            }}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{service.title} - How It Works</DialogTitle>
                <DialogDescription>
                  <ul className="mt-4 space-y-4">
                    {service.steps.map((step, index) => (
                      <li key={index} className="flex gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-woost-soft-blue text-woost-blue flex items-center justify-center text-sm font-medium">
                          {index + 1}
                        </div>
                        <div>
                          <h4 className="font-medium">{step.title}</h4>
                          <p className="text-sm text-gray-600">{step.description}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </DialogDescription>
              </DialogHeader>
            </DialogContent>
          </Dialog>
        ))}
      </div>
    </section>
  );
};

export default ServicesSection;
